import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, RefreshCw, Send, MessageCircle, Crown, Loader2, Zap, TrendingUp, Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { safeDistanceToNow } from "@/lib/dates";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { AppearanceManager } from "@/components/AppearanceManager";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function SettingsPage() {
  const { user } = useAuth();
  const [ranking, setRanking] = useState(null);
  const [execs, setExecs] = useState([]);
  const [tg, setTg] = useState(null);
  const [wa, setWa] = useState(null);
  const [llm, setLlm] = useState(null);
  const [open, setOpen] = useState(false);
  const [newExec, setNewExec] = useState({ email: "", name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [r, e, t, w, u] = await Promise.all([
        api.get("/settings/ranking"),
        api.get("/auth/executives"),
        api.get("/settings/telegram-status").catch(() => ({ data: null })),
        api.get("/settings/whatsapp-status").catch(() => ({ data: null })),
        api.get("/llm-usage/summary"),
      ]);
      setRanking(r.data); setExecs(e.data); setTg(t.data); setWa(w.data); setLlm(u.data);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const saveRanking = async () => {
    setSaving(true);
    try {
      await api.put("/settings/ranking", ranking);
      toast.success("Ranking config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resetRanking = async () => {
    try { const r = await api.post("/settings/ranking/reset"); setRanking(r.data); toast.success("Reset to defaults"); }
    catch (e) { toast.error("Failed"); }
  };

  const addExec = async () => {
    if (!newExec.email) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      await api.post("/auth/executives", newExec);
      toast.success("Executive added. they can now request a magic link");
      setOpen(false); setNewExec({ email: "", name: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const removeExec = async (id) => {
    try { await api.delete(`/auth/executives/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed"); throw e; }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Settings" subtitle="Ranking weights, executives, WhatsApp and Telegram bots, appearance" />

      <UpkeepRateCard />

      {/* Appearance (master admin only) */}
      {user?.is_master && <AppearanceManager />}

      {/* WhatsApp status (primary) */}
      <Card className="rounded-xl" data-testid="whatsapp-status-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-[hsl(var(--ap-success))]" /> WhatsApp bot
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Primary staff interface. Strings are editable on the WhatsApp Editor page.</p>
        </CardHeader>
        <CardContent className="text-sm space-y-2" data-testid="whatsapp-status">
          <Row label="Display number" value={wa?.display_number || ""} />
          <Row label="Phone number ID" value={wa?.phone_number_id || ""} mono />
          <Row label="Webhook URL" value={wa?.webhook_url || ""} mono />
          <Row label="Bound staff numbers" value={wa?.bound_staff_numbers ?? 0} />
          <Row label="Total ingests" value={wa?.total_ingests ?? 0} />
          <Row label="Last activity" value={wa?.last_ingest_at ? `${safeDistanceToNow(wa.last_ingest_at)} ago` : "none yet"} />
          <div className="flex items-center gap-2 mt-2">
            <span
              className={`h-2 w-2 rounded-full ${wa?.configured ? "bg-[hsl(var(--ap-success))] animate-pulse" : "bg-muted-foreground"}`}
              data-testid="whatsapp-status-indicator"
            />
            <span className="text-xs text-muted-foreground">
              {wa?.configured ? "Cloud API token configured" : "Cloud API token missing"}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Telegram status (secondary / legacy) */}
      <Card className="rounded-xl">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4 text-[hsl(var(--ap-info))]" /> Telegram bot <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2">legacy</span></CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2" data-testid="telegram-status">
          <Row label="Bot username" value={tg?.bot_username ? `@${tg.bot_username}` : ""} />
          <Row label="Webhook URL" value={tg?.webhook_url || ""} mono />
          <Row label="Total ingestions" value={tg?.total_ingests ?? 0} />
          <Row label="Last activity" value={tg?.last_ingest_at ? `${safeDistanceToNow(tg.last_ingest_at)} ago` : "none yet"} />
          <div className="flex items-center gap-2 mt-2">
            <span className={`h-2 w-2 rounded-full ${tg?.last_ingest_at ? "bg-[hsl(var(--ap-success))] animate-pulse" : "bg-muted-foreground"}`} data-testid="telegram-status-indicator" />
            <span className="text-xs text-muted-foreground">Webhook connected on app startup</span>
          </div>
        </CardContent>
      </Card>

      {/* LLM usage / credit meter */}
      <Card className="rounded-xl" data-testid="llm-usage-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-[hsl(var(--ap-brass))]" /> LLM usage & credit meter</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => load()} data-testid="llm-refresh-button"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Estimated raw OpenAI cost for AI calls (feed parsing, invoice vision, horse/staff parsing). Emergent Universal Key markup may differ.</p>
        </CardHeader>
        <CardContent>
          {!llm ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <UsageCard label="Today" value={`$${(llm.today?.cost_usd || 0).toFixed(4)}`} sub={`${llm.today?.calls || 0} calls`} testId="llm-today" />
                <UsageCard label="Month-to-date" value={`$${(llm.month_to_date?.cost_usd || 0).toFixed(4)}`} sub={`${llm.month_to_date?.calls || 0} calls`} testId="llm-mtd" />
                <UsageCard label="Projected month" value={`$${(llm.projected_monthly_usd || 0).toFixed(4)}`} sub="based on MTD pace" highlight testId="llm-projected" icon={TrendingUp} />
                <UsageCard label="Last 30 days" value={`$${(llm.last_30d?.cost_usd || 0).toFixed(4)}`} sub={`${llm.last_30d?.calls || 0} calls · ${llm.last_30d?.success_rate ?? 100}% ok`} testId="llm-30d" />
              </div>
              {llm.series_14d?.length > 0 && (
                <div className="h-[140px]" data-testid="llm-series-chart">
                  <ResponsiveContainer>
                    <BarChart data={llm.series_14d}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `$${v.toFixed(3)}`} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }} formatter={(v, n) => n === "cost_usd" ? [`$${Number(v).toFixed(4)}`, "cost"] : [v, n]} />
                      <Bar dataKey="cost_usd" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Breakdown by purpose (MTD)</div>
                {Object.entries(llm.month_to_date?.by_purpose || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No AI calls this month yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {Object.entries(llm.month_to_date?.by_purpose || {}).map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{k.replace("_", " ")}</span>
                        <span className="font-mono">{v.count} × → ${v.cost_usd.toFixed(4)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground border-t pt-3">{llm.pricing_note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Executives */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Executives</CardTitle>
          {user?.is_master && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="exec-add-button"><Plus className="h-4 w-4 mr-2" /> Add executive</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Invite executive</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Email</Label><Input type="email" value={newExec.email} onChange={(e) => setNewExec({...newExec, email: e.target.value})} data-testid="exec-email-input" /></div>
                  <div><Label>Name (optional)</Label><Input value={newExec.name} onChange={(e) => setNewExec({...newExec, name: e.target.value})} /></div>
                  <p className="text-xs text-muted-foreground">They will be able to sign in via magic link sent to their email.</p>
                </div>
                <DialogFooter><Button onClick={addExec} disabled={saving} data-testid="exec-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invite"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="execs-list">
            {execs.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-3 rounded-md border">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--ap-forest))] text-[hsl(var(--ap-paper))] text-xs font-medium">{(e.name || e.email).slice(0,1).toUpperCase()}</div>
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">{e.name || e.email.split("@")[0]}{e.is_master && <Crown className="h-3.5 w-3.5 text-[hsl(var(--ap-brass))]" />}</div>
                    <div className="text-xs text-muted-foreground">{e.email}</div>
                  </div>
                </div>
                {user?.is_master && !e.is_master && (
                  <ConfirmDialog
                    title={`Remove executive access for ${e.name || e.email}?`}
                    description={
                      <>
                        <p><span className="font-medium text-foreground">{e.email}</span> will lose all access to the dashboard immediately.</p>
                        <p className="mt-2 text-xs">You can add them back later — their Clerk sessions will be invalidated immediately.</p>
                      </>
                    }
                    confirmLabel="Yes, remove access"
                    onConfirm={() => removeExec(e.id)}
                    testId={`exec-remove-${e.id}`}
                  >
                    <Button variant="ghost" size="icon" data-testid={`exec-remove-${e.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </ConfirmDialog>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ranking */}
      <Card className="rounded-xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Ranking Formula v9. weights & thresholds</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Master admin only. Editing rebalances horse leaderboard.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={resetRanking} data-testid="ranking-reset-button"><RefreshCw className="h-4 w-4 mr-2" /> Reset</Button>
            <Button size="sm" onClick={saveRanking} disabled={saving || !user?.is_master} data-testid="ranking-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </div>
        </CardHeader>
        <CardContent>
          {!ranking ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Weights</h4>
                {Object.entries(ranking.weights).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3 mb-3">
                    <Label className="w-24 text-sm">{({P: "Performance", C: "Consistency", R: "Rider", L: "Level", D: "Durability", A: "Accuracy", F: "Fatigue"})[k]}</Label>
                    <Input type="number" step="0.01" min="0" max="1" value={v} onChange={(e) => setRanking({ ...ranking, weights: { ...ranking.weights, [k]: parseFloat(e.target.value) } })} className="w-24 font-mono" disabled={!user?.is_master} data-testid={`weight-${k}`} />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">Sum: <span className="font-mono">{Object.values(ranking.weights).reduce((a, b) => a + b, 0).toFixed(2)}</span></p>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Thresholds (score)</h4>
                {Object.entries(ranking.thresholds).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3 mb-3">
                    <Label className="w-32 text-sm capitalize">{k}+</Label>
                    <Input type="number" min="0" max="100" value={v} onChange={(e) => setRanking({ ...ranking, thresholds: { ...ranking.thresholds, [k]: parseFloat(e.target.value) } })} className="w-24 font-mono" disabled={!user?.is_master} data-testid={`threshold-${k}`} />
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                      <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
                      {(() => {
                        const label = { elite: "Elite", commercial: "Good", development: "Average+", green: "Below Average" }[k];
                        const count = { elite: 5, commercial: 4, development: 3, green: 2 }[k] || 0;
                        return (
                          <>
                            {label}
                            <span className="inline-flex items-center gap-[1px] ml-1">
                              {Array.from({ length: count }).map((_, i) => (
                                // eslint-disable-next-line react/no-array-index-key -- fixed-count decorative stars with no distinguishing identity
                                <Star key={`${k}-star-${i}`} className="h-2.5 w-2.5 fill-current" strokeWidth={1.5} />
                              ))}
                            </span>
                          </>
                        );
                      })()}
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-3 mt-3">
                  <Label className="w-32 text-sm">Rides window</Label>
                  <Input type="number" min="3" max="100" value={ranking.last_n_chukkas} onChange={(e) => setRanking({ ...ranking, last_n_chukkas: parseInt(e.target.value) })} className="w-24 font-mono" disabled={!user?.is_master} data-testid="chukkas-window" />
                  <span className="text-xs text-muted-foreground">most recent rides</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b last:border-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={`${mono ? "font-mono text-xs" : ""} text-foreground break-all"`}>{value}</span>
    </div>
  );
}

function UsageCard({ label, value, sub, highlight, testId, icon: Icon }) {
  return (
    <div className={`rounded-md border p-3 ${highlight ? "border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.05)]" : ""}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="font-serif text-2xl tabular-nums leading-none mt-1.5" data-testid={`${testId}-value`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}


// Global horse upkeep rate — used to compute the live "Cost so far" column
// on the Horses page. Gated by `horses:view_cost` (master implicit).
function UpkeepRateCard() {
  const { hasPerm, isMaster } = useAuth();
  const canManage = isMaster || hasPerm?.("horses:view_cost");
  const [cfg, setCfg] = useState(null);
  const [rate, setRate] = useState("");
  const [currency, setCurrency] = useState("ZAR");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    api.get("/horse-upkeep/rate").then((r) => {
      setCfg(r.data);
      setRate(r.data?.monthly_rate ? String(r.data.monthly_rate) : "");
      setCurrency(r.data?.currency || "ZAR");
    }).catch(() => {});
  }, [canManage]);

  if (!canManage) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/horse-upkeep/rate", { monthly_rate: Number(rate) || 0, currency });
      setCfg(res.data);
      toast.success("Upkeep rate saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rate");
    } finally { setSaving(false); }
  };

  return (
    <Card className="rounded-xl" data-testid="upkeep-rate-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[hsl(var(--ap-brass))]" /> Horse upkeep rate
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Daily upkeep cost per horse. The Horses page shows each horse's live <strong>Cost so far</strong> = this rate × days at the farm.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Daily rate per horse</Label>
            <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="w-40" data-testid="upkeep-rate-input" placeholder="e.g. 150" />
          </div>
          <div>
            <Label className="text-xs">Currency</Label>
            <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} className="w-24" data-testid="upkeep-currency-input" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="upkeep-rate-save"
            className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
          </Button>
          {cfg?.updated_at && (
            <span className="text-[11px] text-muted-foreground">Updated {safeDistanceToNow(cfg.updated_at, { addSuffix: true })}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
